import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { useContext, useMemo } from 'react';
import Button from '@atoms/Button/Button';
import Divider from '@atoms/Divider/Divider';
import Logo from '@atoms/Logo/Logo';
import Section from '@atoms/Section/Section';
import Title from '@atoms/Title/Title';
import FooterCTABanner from '@components/FooterCTABanner/FooterCTABanner';
import SignupModal from '@components/SignupModal/SignupModal';
import SocialMediaLinks from '@components/SocialMediaLinks/SocialMediaLinks';
import { GlobalContext } from '@context/GlobalContext';
import SignupFormIndividual from '../../landingPage/SignupFormIndividual';
import NavFooter from '../Nav/NavFooter';
import LanguageSwitcher from '../translation';
import FooterAddress from './FooterAddress';
import './_Footer.scss';
import { useStaticQuery, graphql, Link } from 'gatsby';
import { getOriginalLocale } from '@helpers/GlobalHelpers';
import { useLocale } from '../../hooks';
import { UserLang } from "@constants/common";
import { ModalContext, DialogMode } from "@context/modal";
import ProviderNewsletterFormModal from "@components/ProviderNewsletterFormModal";

Footer.defaultProps = {
    theme: 'dark', // light
    variant: 'Simple', // extended, minimal
};

function Footer(props) {
    const graphqlData = useStaticQuery(graphql`
        query {
            aepsyCms {
                locationsDE: locations(pagination: { limit: 100 }, locale: "de-CH") {
                    slug
                    locale
                    navLabel
                }
                locationsEN: locations(pagination: { limit: 100 }, locale: "en") {
                    slug
                    locale
                    navLabel
                }
                locationsFR: locations(pagination: { limit: 100 }, locale: "fr-CH") {
                    slug
                    locale
                    navLabel
                }
                locationsIT: locations(pagination: { limit: 100 }, locale: "it") {
                    slug
                    locale
                    navLabel
                }
            }
        }
    `);

    const { currentLanguage } = useLocale();
    const { t } = useTranslation();
    const { showModal, hideModal } = useContext(ModalContext);

    const mod_class = [
        'Footer',
        props.theme !== undefined ? 'theme-' + props.theme : '',
    ].join(' ');

    const [globalState, setGlobalState] = useContext(GlobalContext);

    function handleNewsletterClick() {
        if (typeof window !== 'undefined') {
            const [_, locale, slug] = window.location.pathname.split('/');
            if (slug === 'for-therapists') {
                showModal(<ProviderNewsletterFormModal onClose={hideModal} defaultSpecialInterest="" />, {
                    mode: DialogMode.Custom,
                    className: "ProviderNewsletterFormModal",
                });
            } else {
                setGlobalState((prevData) => ({
                    ...prevData,
                    newsletterModalIsOpen: !globalState.newsletterModalIsOpen,
                }));
            }
        }

    }

    const locationLinks = useMemo(() => {
        const {
            aepsyCms,
        } = graphqlData;
        if (!aepsyCms) return [];

        switch (currentLanguage) {
            case UserLang.English:
                return aepsyCms.locationsEN;
            case UserLang.French:
                return aepsyCms.locationsFR;
            case UserLang.Italian:
                return aepsyCms.locationsIT;
            default:
                return aepsyCms.locationsDE;
        }
    }, [graphqlData, currentLanguage]);

    return (
        <div>
            {props.variant === 'extended' ? <FooterCTABanner /> : ''}
            <div className={mod_class}>
                <Section spacingTop="m" spacingBottom="s" container="large">
                    <div className="Footer--item g_1_3">
                        <Logo theme="white" />
                    </div>
                    <div className="Footer--item g_1_3">
                        <NavFooter />
                    </div>
                    <div className="NavFooter Footer--item g_1_3">
                        <div className="Footer--item--block">
                            <Title tag="h4" size="s" theme="white">
                                {t('footer.follow.us.on')}
                            </Title>
                            <SocialMediaLinks />
                        </div>
                        <div className="Footer--item--block">
                            <Title tag="h4" size="s" theme="white">
                                {t('footer.contact')}
                            </Title>
                            <a
                                className="Nav--item Nav--item-contact"
                                href="mailto:care@aepsy.com"
                            >
                                care@aepsy.com
                            </a>
                        </div>
                        <div className="Footer--item--block">
                            <Title tag="h4" size="s" theme="white">
                                {t('footer.newsletter')}
                            </Title>
                            <Divider invisible spacing="xxxs" />
                            <Button
                                theme="white"
                                label={t('footer.newsletter.cta')}
                                onClick={() => handleNewsletterClick()}
                            />

                            <SignupModal>
                                <SignupFormIndividual />
                            </SignupModal>
                        </div>
                        <div className="Footer--item--block">
                            <Title tag="h4" size="s" theme="white">
                                {t('footer.language')}
                            </Title>
                            <Divider invisible spacing="xxxs" />
                            <LanguageSwitcher
                                id="LanguageSwitcherFooter"
                                variant="full"
                                theme="white"
                            />
                        </div>
                        {locationLinks && locationLinks.length > 0 ? (
                            <div className="Footer--item--block">
                                <Title tag="h4" size="s" theme="white">
                                    {t('nav.label.by.cities')}
                                </Title>
                                <Divider invisible spacing="xxxs" />
                                {locationLinks.map((item) => (
                                    <Link
                                        key={item?.slug}
                                        to={`/${getOriginalLocale(
                                            item?.locale
                                        )}/${item?.slug}`}
                                    >
                                        <Title
                                            size="m"
                                            theme="white-lighter"
                                            noMargin
                                        >
                                            {item?.navLabel}
                                        </Title>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <></>
                        )}
                    </div>
                </Section>

                <FooterAddress />
            </div>
        </div>
    );
}

export default Footer;
